/**
 * @fileoverview Pointer-driven click/drag gesture for selecting a single month
 * (click) or a contiguous month range (drag) in a month-button grid.
 *
 * Tracking uses always-attached per-button React handlers so it works reliably
 * inside portaled/overlay UIs (e.g. a Radix dropdown): `onPointerEnter` advances
 * the range for mouse, and `onPointerMove` + `elementFromPoint` covers touch,
 * where implicit pointer capture keeps move events on the origin button. Release
 * (and release outside the grid) is handled by window-level `pointerup`/`pointercancel`.
 * @module shared/hooks/useMonthRangeDrag
 */
import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react'

/** Live preview of the in-progress drag, as inclusive month indices. */
export interface DragRange {
  lo: number
  hi: number
}

interface UseMonthRangeDragOptions {
  /** Called with the normalised inclusive month index span on gesture end. */
  onCommit: (loIdx: number, hiIdx: number) => void
}

interface MonthProps {
  'data-month-idx': number
  onPointerDown: (e: PointerEvent<HTMLElement>) => void
  onPointerEnter: (e: PointerEvent<HTMLElement>) => void
  onPointerMove: (e: PointerEvent<HTMLElement>) => void
}

interface UseMonthRangeDragResult {
  /** Non-null while dragging: the span the user would commit if they released now. */
  dragRange: DragRange | null
  getMonthProps: (idx: number) => MonthProps
}

/** Normalise two indices into an inclusive lo/hi span. */
function span(a: number, b: number): DragRange {
  return { lo: Math.min(a, b), hi: Math.max(a, b) }
}

/** Resolve the month index under the pointer via the DOM (touch drag path). */
function monthIdxAtPoint(x: number, y: number): number | null {
  const el = document.elementFromPoint(x, y)
  const btn = el?.closest('[data-month-idx]')
  if (!btn) return null
  const raw = btn.getAttribute('data-month-idx')
  if (raw === null) return null
  const n = Number.parseInt(raw, 10)
  return Number.isNaN(n) ? null : n
}

export function useMonthRangeDrag({
  onCommit,
}: UseMonthRangeDragOptions): UseMonthRangeDragResult {
  const anchorRef = useRef<number | null>(null)
  const currentRef = useRef<number | null>(null)
  const [dragRange, setDragRange] = useState<DragRange | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const reset = useCallback((): void => {
    anchorRef.current = null
    currentRef.current = null
    setDragRange(null)
  }, [])

  /** Advance the live range to include `idx` if a drag is active. */
  const extendTo = useCallback((idx: number): void => {
    if (anchorRef.current === null || idx === currentRef.current) return
    currentRef.current = idx
    setDragRange(span(anchorRef.current, idx))
  }, [])

  // Release is handled at the window level so a drag released OUTSIDE the grid
  // still commits the last in-grid span (instead of leaving a stuck highlight).
  // Tracking does NOT go through window — per-button handlers below own that.
  useEffect(() => {
    if (!isDragging) return
    const handleUp = (): void => {
      const anchor = anchorRef.current
      const current = currentRef.current
      reset()
      setIsDragging(false)
      if (anchor !== null && current !== null) {
        const { lo, hi } = span(anchor, current)
        onCommit(lo, hi)
      }
    }
    const handleCancel = (): void => {
      reset()
      setIsDragging(false)
    }
    window.addEventListener('pointerup', handleUp)
    window.addEventListener('pointercancel', handleCancel)
    return () => {
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleCancel)
    }
  }, [isDragging, onCommit, reset])

  const getMonthProps = useCallback(
    (idx: number): MonthProps => ({
      'data-month-idx': idx,
      onPointerDown: (): void => {
        anchorRef.current = idx
        currentRef.current = idx
        setDragRange({ lo: idx, hi: idx })
        setIsDragging(true)
      },
      // Mouse: entering a sibling button while dragging extends the range.
      onPointerEnter: (): void => {
        extendTo(idx)
      },
      // Touch: implicit pointer capture keeps moves on the origin button, so
      // resolve the actually-hovered month from the pointer coordinates.
      onPointerMove: (e: PointerEvent<HTMLElement>): void => {
        if (anchorRef.current === null || e.pointerType !== 'touch') return
        const over = monthIdxAtPoint(e.clientX, e.clientY)
        if (over !== null) extendTo(over)
      },
    }),
    [extendTo]
  )

  return { dragRange, getMonthProps }
}
